import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FancyMultiSelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  SelectType,
  Switch,
  Textarea,
  TimeDurationPicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { isNonEmpty } from '@ais-ui/utils';
import { AreaDetectorFormValues } from './areaForm';
import { useActivityFilters } from '../detectorUtils';
import { useShapeFetch } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { AngleInput } from '../../../../components/AngleInput';
import { usePermissions } from '../../../Permissions/PermissionsProvider';

interface AreaAdvancedFormProps {
  form: UseFormReturn<AreaDetectorFormValues>;
}

export const AreaAdvancedForm = ({ form }: AreaAdvancedFormProps) => {
  const { messages } = useLocalization();
  const [checkDirection, setCheckDirection] = useState(
    isNonEmpty(form.getValues('directionMinAngle')) ||
      isNonEmpty(form.getValues('directionMaxAngle')),
  );
  const permissions = usePermissions();
  const activityFilters = useActivityFilters();
  const [contentHeight, setContentHeight] = useState(0);
  const [isFancyMultiSelectOpen, setIsFancyMultiSelectOpen] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | null>(null);

  // We had a problem where the height of the content was not being updated when the FancyMultiSelect was open
  // so the content of the FancyMultiSelect was hidden. Now we make the content height dynamic based on the state of the FancyMultiSelect
  const changeContentHeight = () => {
    if (
      accordionValue === 'advanced-activities-item' &&
      isFancyMultiSelectOpen
    ) {
      setContentHeight(320); // Add padding for dropdown
    } else {
      setContentHeight(0);
    }
  };

  useEffect(() => {
    changeContentHeight();
  }, [isFancyMultiSelectOpen, accordionValue]);

  const handleOpenChange = (isOpen: boolean) => {
    setIsFancyMultiSelectOpen(isOpen);
  };

  const handleValueChange = (value: string) => {
    // Only update height if the Activities section is being opened
    setAccordionValue(value);
  };
  let isCircleSelected = false;
  const shapeFetch = useShapeFetch();
  const shapeId = useMemo(() => form.watch('shapeId'), [form]);
  const [shape, setShape] = useState<GeoJson | null>(null);

  useEffect(() => {
    if (!shapeId) return;

    const fetchShape = async () => {
      const shape = await shapeFetch(shapeId);
      setShape(shape);
    };
    fetchShape();
  }, [shapeId]);

  if (shape && shape?.geometry?.type === 'Point' && shape.properties?.radius) {
    isCircleSelected = true;
  }

  return (
    <div className="space-y-8">
      <Accordion type="single" collapsible onValueChange={handleValueChange}>
        <AccordionItem value="advanced-text-item">
          <AccordionTrigger>Texts</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <FormField
                control={form.control}
                name="activationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.eventdetectorsSaveAdvancedActivationTextLabel}
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_activationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedActivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deactivationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedDeactivationTextLabel
                        }
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_deactivationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedDeactivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-activation-times-item">
          <AccordionTrigger>Activation times</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={'timeBeforeActivation'}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'timeBeforeDeactivation'}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={'timeBetweenTrigger'}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'timeoutWhenActive'}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-direction-item">
          <AccordionTrigger>Direction</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="flex flex-col space-y-4">
                <div className="flex justify-between mr-2">
                  <Label>
                    {messages.eventdetectorsSaveAdvancedCheckDirectionLabel}
                  </Label>
                  <InfoIconTooltip>
                    {
                      messages.eventdetectorsSaveAdvancedCheckDirectionTooltipTextTooltip
                    }
                  </InfoIconTooltip>
                </div>
                <Switch
                  id="eventDetectorAdvanced_checkDirection"
                  checked={checkDirection}
                  onCheckedChange={(checked) => {
                    setCheckDirection(checked);
                    form.setValue('directionMinAngle', undefined);
                    form.setValue('directionMaxAngle', undefined);
                  }}
                ></Switch>
              </div>
              {checkDirection && (
                <div className="flex justify-between space-x-4">
                  <FormField
                    control={form.control}
                    name="directionMinAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMinAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="directionMaxAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMaxAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        {/*<AccordionWithSelect />*/}
        <AccordionItem value="advanced-activities-item">
          <AccordionTrigger>Activities</AccordionTrigger>
          <AccordionContent
            className="transition-all duration-200 ease-out"
            style={{ minHeight: contentHeight ? `${contentHeight}px` : '0' }}
          >
            <div className="space-y-6 px-px">
              <FormField
                control={form.control}
                name="activityFilters"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedActivityFiltersLabel
                        }
                      </FormLabel>
                      <button
                        type="button"
                        className="ml-2 text-sm text-black-500 hover:underline"
                        onClick={() => field.onChange([])}
                      >
                        Clear All Filters
                      </button>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <FancyMultiSelect
                        value={field.value as SelectType[]}
                        onChange={field.onChange}
                        availableSelectables={activityFilters}
                        placeholder={
                          messages.eventdetectorsSaveAdvancedActivityFiltersPlaceholder
                        }
                        onOpenChange={handleOpenChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form
                .watch('activityFilters')
                ?.some(
                  (filter: SelectType) => filter.value === 'transponder_off',
                ) && (
                <FormField
                  control={form.control}
                  name="transponderOffDuration"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between mr-2">
                        <FormLabel>Transponder Off Duration</FormLabel>
                        <InfoIconTooltip>
                          Minimum duration the transponder needs to be off to
                          trigger the detector
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-vessel-trigger-item">
          <AccordionTrigger>Vessel trigger</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="useVesselDimensions"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-4">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="entireVesselInside"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedEntireVesselInsideLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedEntireVesselInsideTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-notifications-item">
          <AccordionTrigger>Notifications</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="flex flex-col space-y-4">
                <Label>
                  {messages.eventdetectorsSaveAdvancedSendShipNotificationLabel}
                </Label>
                <div className="grid grid-cols-[auto_1fr] gap-4">
                  <FormField
                    control={form.control}
                    name="sendShipNotification"
                    render={({ field }) => (
                      <FormItem className="flex flex-col space-y-4">
                        <FormControl onChange={() => form.clearErrors()}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Switch
                                  checked={field.value}
                                  disabled={!permissions.sendMessages}
                                  onCheckedChange={(value) => {
                                    if (permissions.sendMessages) {
                                      field.onChange(value);
                                      form.setValue('shipNotificationText', '');
                                    }
                                  }}
                                />
                              </div>
                            </TooltipTrigger>
                            {!permissions.sendMessages && (
                              <TooltipContent>
                                <p>
                                  You need permission to send messages to ships
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.getValues('sendShipNotification') &&
                    permissions.sendMessages && (
                      <FormField
                        control={form.control}
                        name={'shipNotificationText'}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl onChange={() => form.clearErrors()}>
                              <Textarea
                                value={field.value}
                                onChange={field.onChange}
                              ></Textarea>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-cpa-tcpa-item">
          <AccordionTrigger>
            <div
              className="flex"
              title="Closest Point of Approach (CPA) and Time to Closest Point of Approach (TCPA)"
            >
              CPA and TCPA
            </div>
            {!isCircleSelected && (
              <span className="text-muted-foreground text-sm ml-2">
                Requires circle shape
              </span>
            )}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="checkCpa"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-2">
                        <FormLabel>Check CPA</FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedSendShipCpaTcpaEnable
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                          disabled={!isCircleSelected}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cpaLimit"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-2">
                        <FormLabel>CPA Limit</FormLabel>
                        <InfoIconTooltip>
                          {messages.eventdetectorsSaveAdvancedSendShipCpaLimit}
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Input
                          className="w-48"
                          type="number"
                          id="eventdetector-cpa-limit"
                          value={field.value}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            field.onChange(value);
                          }}
                          min={0}
                          disabled={!isCircleSelected}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tcpaLimit"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-2">
                        <FormLabel>TCPA Limit</FormLabel>
                        <InfoIconTooltip>
                          {messages.eventdetectorsSaveAdvancedSendShipTcpaLimit}
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Input
                          className="w-48"
                          type="number"
                          id="eventdetector-tcpa-limit"
                          value={field.value}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            field.onChange(value);
                          }}
                          min={0}
                          disabled={!isCircleSelected}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
